import { makeAutoObservable, runInAction } from 'mobx';

import {
  adminLoginAdminToAdminUserDto,
  apiClient,
  clearTokens,
  getAccessToken,
  getRefreshToken,
  onRefreshFailure,
  refresh,
  setTokens,
  toAdminUserDto,
} from '../api';
import type {
  AcceptInviteResultDto,
  AdminLoginResultDto,
  AdminLoginTokensResultDto,
  AdminLoginTotpEnrollmentRequiredResultDto,
  AdminLoginTotpRequiredResultDto,
  AdminPasswordResetConfirmResultDto,
  AdminPasswordResetRequestResultDto,
  AdminUserDto,
  AuthTokensDto,
} from '../api';
import { decodeJwtPayload } from '../utils/decodeJwtPayload';

/**
 * Auth store — owns the current admin user + session lifecycle.
 *
 * v1 + AUT-110 scope:
 * - `currentAdmin` / `isAuthenticated` / `loading` / `error` / `hydrated`
 * - `hydrate()` — bootstrap on app start; restores the session from the
 *   persisted refresh token (rehydrating permissions from the access
 *   token's JWT claim).
 * - `loginWithPassword(email, password)` — POSTs `/admin/auth/login`;
 *   handles the tagged-union response internally and EITHER completes
 *   the login (tokens / totp_enrollment_required branches) OR resolves
 *   with a `tempToken` so the LoginPage can advance to the TOTP step.
 * - `acceptInvite(token, password)` — POSTs `/admin/invites/accept`,
 *   completes login on either branch.
 * - `requestPasswordReset(email)` — POSTs `/admin/auth/password/reset/request`.
 *   Always resolves on 200 (anti-enum); rejects on 429 throttle.
 * - `confirmPasswordReset(token, password)` — POSTs
 *   `/admin/auth/password/reset/confirm`. Resolves on 200; rejects on
 *   4xx mapped errors (token-invalid / policy-violation).
 * - `completeLogin(tokens)` — terminal success path for the legacy
 *   `/auth/totp/challenge` round-trip (TOTP-required branch).
 * - `forceLogout()` — local-only teardown; subscribed to onRefreshFailure.
 * - `logout()` — POSTs /auth/logout (best-effort) then forceLogout.
 */
export type LoginWithPasswordOutcome =
  | { kind: 'tokens' }
  | { kind: 'totp_required'; tempToken: string }
  | { kind: 'totp_enrollment_required' };

export class AuthStore {
  currentAdmin: AdminUserDto | null = null;
  isAuthenticated = false;
  loading = false;
  error: string | null = null;
  hydrated = false;

  private unsubscribeRefreshFailure: (() => void) | null = null;

  private logoutInFlight: Promise<void> | null = null;

  private hydrating: Promise<void> | null = null;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
    this.unsubscribeRefreshFailure = onRefreshFailure(() => {
      void this.forceLogout();
    });
  }

  dispose(): void {
    this.unsubscribeRefreshFailure?.();
    this.unsubscribeRefreshFailure = null;
  }

  /**
   * Called once on app boot (from `<AuthGate>`). Restores the session
   * from the persisted refresh token so an F5 / reload doesn't bounce the
   * operator back to `/login` and doesn't strip their permissions.
   *
   * Flow:
   *   1. Read the `sessionStorage` refresh token (via tokenStorage).
   *   2. If present → `refresh()` (coalesced) to mint a fresh access
   *      token; `doRefresh` persists it in-memory + rotates the refresh
   *      token.
   *   3. On success → decode the NEW access token's `permissions` claim
   *      (NOT the refresh response body — its `user` is a plain `UserDto`
   *      with no permissions, and `toAdminUserDto` stubs them to `[]`,
   *      which would fail every gated page closed). Build `currentAdmin`
   *      from the decoded `sub` + filtered `permissions`; `phone`/name
   *      stay `null` on rehydrate (same tradeoff the invite/login mapper
   *      already accepts).
   *   4. On failure / no-token → clear tokens + logged-out.
   *
   * `hydrated` flips in a `finally` so `<AuthGate>` keeps the spinner up
   * until refresh settles and ALWAYS resolves the gate (no infinite
   * spin). Single-flight via `hydrating` guards React StrictMode's
   * double `useEffect` fire.
   */
  async hydrate(): Promise<void> {
    if (this.hydrated) return;
    if (this.hydrating) return this.hydrating;
    this.hydrating = (async () => {
      try {
        if (!getRefreshToken()) {
          this.forceLogout();
          return;
        }
        const outcome = await refresh();
        if (!outcome.ok) {
          // `doRefresh` already clears tokens on http_error; forceLogout
          // is idempotent and also resets the observables.
          this.forceLogout();
          return;
        }
        this.applyRehydratedSession();
      } finally {
        runInAction(() => {
          this.hydrated = true;
        });
        this.hydrating = null;
      }
    })();
    return this.hydrating;
  }

  /**
   * Rebuild `currentAdmin` from the freshly-minted access token's JWT
   * claims. If the token can't be decoded (shouldn't happen — it just
   * came from our API) we fail closed to logged-out rather than seat a
   * permission-less session.
   */
  private applyRehydratedSession(): void {
    const decoded = decodeJwtPayload(getAccessToken());
    if (!decoded) {
      this.forceLogout();
      return;
    }
    runInAction(() => {
      this.currentAdmin = {
        id: decoded.sub,
        phone: null,
        firstName: null,
        lastName: null,
        permissions: decoded.permissions,
      };
      this.isAuthenticated = true;
      this.error = null;
    });
  }

  /**
   * POST `/admin/auth/login` with email + password. Resolves with a
   * discriminator the LoginPage uses to decide next-step navigation:
   *
   *   - `tokens` — session live, navigate to Dashboard.
   *   - `totp_required` — caller advances the state machine to the TOTP
   *     step; `tempToken` is round-tripped to `/auth/totp/challenge`.
   *   - `totp_enrollment_required` — tokens stored, session live, but
   *     the role demands a second factor. Caller should route to the
   *     TOTP-enrolment screen (out of scope for v1 — logged as a warning
   *     per plan edge case #7; SPA still navigates to Dashboard so the
   *     destructive surfaces fall behind the TOTP step-up guard).
   *
   * Throws (rejects) on any 4xx / 5xx the SPA should render as an error —
   * the caller (`LoginStep`) maps via `loginErrorMap`.
   */
  async loginWithPassword(email: string, password: string): Promise<LoginWithPasswordOutcome> {
    const response = await apiClient.post<AdminLoginResultDto>('/admin/auth/login', {
      email,
      password,
    });
    const result = response.data;
    if (result.kind === 'tokens') {
      this.completeAdminLogin(result);
      return { kind: 'tokens' };
    }
    if (result.kind === 'totp_required') {
      return this.handleTotpRequired(result);
    }
    return this.handleTotpEnrollmentRequired(result);
  }

  private handleTotpRequired(result: AdminLoginTotpRequiredResultDto): LoginWithPasswordOutcome {
    // Tokens are NOT yet issued. Caller advances to TOTP step + posts
    // `tempToken` to `/auth/totp/challenge`.
    return { kind: 'totp_required', tempToken: result.tempToken };
  }

  private handleTotpEnrollmentRequired(
    result: AdminLoginTotpEnrollmentRequiredResultDto,
  ): LoginWithPasswordOutcome {
    // Real tokens are issued so the SPA can call `/auth/totp/enable`.
    // v1 caveat: enrolment UX deferred — we still complete the login;
    // destructive surfaces stay behind the per-endpoint TOTP step-up
    // guard so worst case is a read-only session.
    this.completeAdminLogin(result);
    if (typeof window !== 'undefined' && import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn(
        '[admin auth] totp_enrollment_required — TOTP enrolment UX deferred (AUT-110 edge case #7).',
      );
    }
    return { kind: 'totp_enrollment_required' };
  }

  /**
   * POST `/admin/invites/accept` with the raw invite token + the chosen
   * password. Server returns either fresh tokens (`kind: 'tokens'`) or
   * tokens + a TOTP-enrolment-required flag (`kind: 'totp_enrollment_required'`).
   * In both branches we complete the login (real tokens are issued — the
   * enrolment UX is per-endpoint via the TOTP step-up guard).
   */
  async acceptInvite(token: string, password: string): Promise<void> {
    const response = await apiClient.post<AcceptInviteResultDto>('/admin/invites/accept', {
      token,
      password,
    });
    // Both branches in `AcceptInviteResultDto` carry the same token base
    // (`accessToken` / `refreshToken` / `admin`) — the discriminator is
    // purely a UX hint.
    this.completeAdminLogin(response.data);
  }

  /**
   * POST `/admin/auth/password/reset/request` — silent 200 on success
   * regardless of whether the email matched a real admin (BR8 anti-enum).
   * Rejects on 429 throttle so the caller can show the wait-period UI.
   */
  async requestPasswordReset(email: string): Promise<AdminPasswordResetRequestResultDto> {
    const response = await apiClient.post<AdminPasswordResetRequestResultDto>(
      '/admin/auth/password/reset/request',
      { email },
    );
    return response.data;
  }

  /**
   * POST `/admin/auth/password/reset/confirm` — submit new password
   * + raw reset token. Resolves on 200; rejects on 400 (token invalid)
   * or 422 (policy violation). Caller surfaces inline error copy.
   *
   * NOTE: server does NOT auto-login the user; SPA must route back to
   * `/auth/login` (architect R2 — TOTP cannot be bypassed).
   */
  async confirmPasswordReset(
    token: string,
    password: string,
  ): Promise<AdminPasswordResetConfirmResultDto> {
    const response = await apiClient.post<AdminPasswordResetConfirmResultDto>(
      '/admin/auth/password/reset/confirm',
      { token, password },
    );
    return response.data;
  }

  /**
   * Terminal success of the legacy `/auth/totp/challenge` round-trip
   * used by `TotpStep` after `POST /admin/auth/login` returns
   * `totp_required`. The challenge endpoint still returns the
   * `AuthTokensDto` shape (with embedded `user`) — kept for backwards
   * compatibility.
   *
   * Persists tokens via tokenStorage and flips the session observables.
   * Callers should `navigate(Routes.App.Dashboard, { replace: true })`
   * from the same `.then()` callback.
   */
  completeLogin(tokens: AuthTokensDto): void {
    setTokens({ accessToken: tokens.accessToken, refreshToken: tokens.refreshToken });
    runInAction(() => {
      this.currentAdmin = toAdminUserDto(tokens.user);
      this.isAuthenticated = true;
      this.hydrated = true;
      this.error = null;
    });
  }

  /**
   * Internal companion to `completeLogin` for the AUT-108+ wire shape
   * (`{ admin: AcceptInviteAdminDto, accessToken, refreshToken, … }`).
   * Used by the password-login + invite-accept paths.
   */
  private completeAdminLogin(payload: {
    accessToken: string;
    refreshToken: string;
    admin: AcceptInviteResultDto['admin'] | AdminLoginTokensResultDto['admin'];
  }): void {
    setTokens({ accessToken: payload.accessToken, refreshToken: payload.refreshToken });
    runInAction(() => {
      this.currentAdmin = adminLoginAdminToAdminUserDto(payload.admin);
      this.isAuthenticated = true;
      this.hydrated = true;
      this.error = null;
    });
  }

  /**
   * Best-effort server logout + local teardown. Single-flight via
   * `logoutInFlight` so a double-tap on the menu item doesn't fire two
   * POSTs.
   */
  async logout(): Promise<void> {
    if (this.logoutInFlight) return this.logoutInFlight;
    this.logoutInFlight = (async () => {
      try {
        await apiClient.post('/auth/logout');
      } catch {
        // Even if the call fails, clear local state.
      }
      this.forceLogout();
    })();
    try {
      await this.logoutInFlight;
    } finally {
      this.logoutInFlight = null;
    }
  }

  /**
   * Called by the refresh-failure handler — no server call, just local
   * teardown. Clears the closure-stored access token + sessionStorage
   * refresh token, then flips observables back to logged-out.
   */
  forceLogout(): void {
    clearTokens();
    runInAction(() => {
      this.currentAdmin = null;
      this.isAuthenticated = false;
      this.error = null;
    });
  }
}
